import {useContext, useEffect, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import {useStore} from '@tanstack/react-form'

import Context from '@s-ui/react-context'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close.js'
import {Field, FIELD_LABEL_SIZES, FIELD_LABEL_STATES} from '@adv-mt/ui/field'
import {Icon, ICON_SIZES} from '@adv-mt/ui/icon'
import {SegmentedButton, SegmentedButtonItem} from '@adv-mt/ui/segmented-button'
import {Textarea, TEXTAREA_RESIZES, TEXTAREA_SIZES, TEXTAREA_STATES} from '@adv-mt/ui/textarea'

import {useContactCardInput} from '../context/ContactSellerV2Context.js'
import {useFormContext} from '../form/formContexts.js'
import {FIELD_ERROR_CODES, MAX_CHARS_MESSAGE, V2_FIELDS} from '../schema/contactSchema.js'
import {useContactFormContext} from './ContactFormContext.js'

export const BASE_CLASS = 'mt-ContactFormV2'

const FIELD_LABEL_SIZE = FIELD_LABEL_SIZES.S
const TEXTAREA_SIZE = TEXTAREA_SIZES.DEFAULT
const TEXTAREA_RESIZE = TEXTAREA_RESIZES.VERTICAL
export const MESSAGE_TITLE_NO_INTENTIONS_KEY = 'CONTACT_SELLER_V2.FORM.MESSAGE_TITLE_NO_INTENTIONS'

const PHONE_MAX_LENGTH = 9
const NAME_MAX_LENGTH = 20
const NON_DIGITS = /\D/g
const toDigitsOnly = value => value.replace(NON_DIGITS, '')

const BLUR_VALIDATED_FIELDS = [V2_FIELDS.EMAIL, V2_FIELDS.PHONE]
const BLUR_VALIDATED_ERRORS = [FIELD_ERROR_CODES.EMAIL_INVALID, FIELD_ERROR_CODES.PHONE_INVALID]

const ERROR_LITERALS = {
  [FIELD_ERROR_CODES.MESSAGE_TOO_LONG]: 'FORM.ALL_FIELDS_ARE_REQUIRED',
  [FIELD_ERROR_CODES.NAME_EMPTY]: 'CONTACT_SELLER_V2.FORM.NAME_ERROR',
  [FIELD_ERROR_CODES.EMAIL_EMPTY]: 'CONTACT_SELLER_V2.FORM.EMAIL_ERROR',
  [FIELD_ERROR_CODES.EMAIL_INVALID]: 'CONTACT_SELLER_V2.FORM.EMAIL_INVALID_ERROR',
  [FIELD_ERROR_CODES.PHONE_INVALID]: 'CONTACT_SELLER_V2.FORM.PHONE_INVALID_ERROR',
  [FIELD_ERROR_CODES.ZIP_CODE_INVALID]: 'FORM.ZIPCODE_TEXT_ERROR',
  [FIELD_ERROR_CODES.TARGET_BUYER_EMPTY]: 'CONTACT_SELLER_V2.FORM.TARGET_BUYER_ERROR'
}

const resolveErrorKey = ({field, domainErrors, domainLiteralKey, hasSubmitted, isEditing = false}) => {
  if (domainErrors?.[field.name]) {
    return domainLiteralKey
  }

  if (isEditing) {
    return undefined
  }

  const {errors, isBlurred} = field.state.meta

  if (!errors?.length) {
    return undefined
  }

  const [error] = errors

  if (!hasSubmitted && !(isBlurred && BLUR_VALIDATED_ERRORS.includes(error))) {
    return undefined
  }

  return ERROR_LITERALS[error]
}

const FieldError = ({id, errorKey}) => {
  const {i18n} = useContext(Context)

  if (!errorKey) {
    return null
  }

  return (
    <Field.Error
      id={id}
      match
      role="alert"
      icon={
        <Icon size={ICON_SIZES.SM}>
          <IconClose size={ICON_SIZES.SM} />
        </Icon>
      }
    >
      {i18n.t(errorKey)}
    </Field.Error>
  )
}

FieldError.propTypes = {id: PropTypes.string, errorKey: PropTypes.string}

const errorIdOf = name => `${name}-error`

const FOCUSABLE_SELECTOR = 'input, textarea, button, [tabindex]'

const focusFirstInvalidField = container => {
  const invalidNode = container?.querySelector('[aria-invalid="true"]')

  if (!invalidNode) {
    return
  }

  const focusTarget = invalidNode.matches(FOCUSABLE_SELECTOR)
    ? invalidNode
    : invalidNode.querySelector(FOCUSABLE_SELECTOR)

  focusTarget?.focus()
}

const FormFields = ({showCommentLabel = true}) => {
  const {i18n} = useContext(Context)
  const form = useFormContext()
  const {
    isZipCodeEnabled = false,
    showTargetBuyerOptions = false,
    userData,
    onTargetBuyerChange
  } = useContactCardInput()
  const {domainErrors = {}, onDomainErrorClear, targetBuyerOptions = []} = useContactFormContext()
  const hasSubmitted = useStore(form.store, state => state.submissionAttempts > 0)
  const submissionAttempts = useStore(form.store, state => state.submissionAttempts)
  const isValid = useStore(form.store, state => state.isValid)
  const fieldsRef = useRef(null)
  const lastFocusedAttemptRef = useRef(0)
  const [editingSinceAttempt, setEditingSinceAttempt] = useState({})

  const isEditing = name => editingSinceAttempt[name] === submissionAttempts

  useEffect(() => {
    const hasFieldErrors = !isValid || Object.keys(domainErrors).length > 0

    if (submissionAttempts === 0 || submissionAttempts === lastFocusedAttemptRef.current || !hasFieldErrors) {
      return
    }

    lastFocusedAttemptRef.current = submissionAttempts
    focusFirstInvalidField(fieldsRef.current)
  }, [submissionAttempts, isValid, domainErrors])

  const changeField = (field, value) => {
    field.handleChange(value)

    if (domainErrors[field.name]) {
      onDomainErrorClear?.(field.name)
    }

    if (BLUR_VALIDATED_FIELDS.includes(field.name) && !isEditing(field.name)) {
      setEditingSinceAttempt(editing => ({...editing, [field.name]: submissionAttempts}))
    }
  }

  const blurField = field => {
    field.handleBlur()

    if (field.name in editingSinceAttempt) {
      setEditingSinceAttempt(({[field.name]: _, ...editing}) => editing)
    }
  }

  const textFieldFor = ({
    name,
    labelKey,
    placeholderKey,
    domainLiteralKey,
    transformValue,
    isRequired = false,
    inputProps
  }) => (
    <form.AppField key={name} name={name}>
      {field => (
        <field.TextField
          errorKey={resolveErrorKey({
            field,
            domainErrors,
            domainLiteralKey,
            hasSubmitted,
            isEditing: isEditing(field.name)
          })}
          inputProps={inputProps}
          isRequired={isRequired}
          labelKey={labelKey}
          placeholderKey={placeholderKey}
          onBlur={() => blurField(field)}
          onValueChange={value => changeField(field, transformValue ? transformValue(value) : value)}
        />
      )}
    </form.AppField>
  )

  const nameField =
    !userData?.name &&
    textFieldFor({
      name: V2_FIELDS.NAME,
      labelKey: 'FORM.CONTACT_NAME_LABEL',
      placeholderKey: 'CONTACT_SELLER_V2.FORM.NAME_PLACEHOLDER',
      domainLiteralKey: 'CONTACT_SELLER_V2.FORM.NAME_ERROR',
      isRequired: true,
      inputProps: {autoComplete: 'name', maxLength: NAME_MAX_LENGTH}
    })

  const phoneField =
    !userData?.phone &&
    textFieldFor({
      name: V2_FIELDS.PHONE,
      labelKey: 'FORM.CONTACT_PHONE_LABEL',
      placeholderKey: 'CONTACT_SELLER_V2.FORM.PHONE_PLACEHOLDER',
      domainLiteralKey: 'CONTACT_SELLER_V2.FORM.PHONE_INVALID_ERROR',
      transformValue: toDigitsOnly,
      inputProps: {type: 'tel', inputMode: 'numeric', autoComplete: 'tel', maxLength: PHONE_MAX_LENGTH}
    })

  const emailField =
    !userData?.email &&
    textFieldFor({
      name: V2_FIELDS.EMAIL,
      labelKey: 'FORM.EMAIL_LABEL',
      placeholderKey: 'CONTACT_SELLER_V2.FORM.EMAIL_PLACEHOLDER',
      domainLiteralKey: 'CONTACT_SELLER_V2.FORM.EMAIL_INVALID_ERROR',
      isRequired: true,
      inputProps: {type: 'email', autoComplete: 'email'}
    })

  return (
    <div ref={fieldsRef} className={`${BASE_CLASS}-fields`}>
      <form.Field name={V2_FIELDS.MESSAGE}>
        {field => {
          const errorKey = resolveErrorKey({field, domainErrors, hasSubmitted})
          const errorId = errorIdOf(field.name)

          return (
            <Field.Root className={`${BASE_CLASS}-field`}>
              {showCommentLabel && (
                <Field.Label
                  highlight
                  size={FIELD_LABEL_SIZE}
                  htmlFor={field.name}
                  state={errorKey ? FIELD_LABEL_STATES.ERROR : undefined}
                >
                  {i18n.t(MESSAGE_TITLE_NO_INTENTIONS_KEY)}
                </Field.Label>
              )}
              <Textarea
                id={field.name}
                name={field.name}
                size={TEXTAREA_SIZE}
                resize={TEXTAREA_RESIZE}
                aria-label={showCommentLabel ? undefined : i18n.t(MESSAGE_TITLE_NO_INTENTIONS_KEY)}
                state={errorKey ? TEXTAREA_STATES.ERROR : undefined}
                aria-invalid={errorKey ? true : undefined}
                aria-describedby={errorKey ? errorId : undefined}
                maxLength={MAX_CHARS_MESSAGE}
                placeholder={i18n.t('FORM.CONTACT_MESSAGE_PLACEHOLDER')}
                value={field.state.value}
                onChange={event => changeField(field, event.target.value)}
                onBlur={field.handleBlur}
              />
              <Field.Counter current={field.state.value.length} max={MAX_CHARS_MESSAGE}>
                {i18n.t('COMMON.PLURALS.CHARACTER', MAX_CHARS_MESSAGE).toLowerCase()}
              </Field.Counter>
              <FieldError id={errorId} errorKey={errorKey} />
            </Field.Root>
          )
        }}
      </form.Field>

      {(nameField || phoneField) && (
        <div className={`${BASE_CLASS}-fieldRow`}>
          {nameField}
          {phoneField}
        </div>
      )}

      {emailField}

      {isZipCodeEnabled &&
        textFieldFor({
          name: V2_FIELDS.ZIP_CODE,
          labelKey: 'FORM.CONTACT_POSTAL_CODE_LABEL',
          placeholderKey: 'FORM.CONTACT_POSTAL_CODE_PLACEHOLDER',
          domainLiteralKey: 'FORM.ZIPCODE_TEXT_ERROR',
          transformValue: toDigitsOnly,
          inputProps: {inputMode: 'numeric', autoComplete: 'postal-code'}
        })}

      {showTargetBuyerOptions && (
        <form.Field name={V2_FIELDS.TARGET_BUYER}>
          {field => {
            const errorKey = resolveErrorKey({field, domainErrors, hasSubmitted})
            const errorId = errorIdOf(field.name)

            return (
              <Field.Root className={`${BASE_CLASS}-field`}>
                <SegmentedButton
                  className={`${BASE_CLASS}-targetBuyer`}
                  fullWidth
                  aria-label={i18n.t('CONTACT_SELLER_V2.FORM.TARGET_BUYER_ARIA_LABEL')}
                  aria-invalid={errorKey ? true : undefined}
                  aria-describedby={errorKey ? errorId : undefined}
                  value={field.state.value ? [field.state.value] : []}
                  onValueChange={([id]) => {
                    if (!id) return

                    changeField(field, id)
                    field.handleBlur()
                    onTargetBuyerChange?.(id)
                  }}
                >
                  {targetBuyerOptions.map(({id, labelKey}) => (
                    <SegmentedButtonItem key={id} value={id}>
                      {i18n.t(labelKey)}
                    </SegmentedButtonItem>
                  ))}
                </SegmentedButton>
                <FieldError id={errorId} errorKey={errorKey} />
              </Field.Root>
            )
          }}
        </form.Field>
      )}
    </div>
  )
}

FormFields.displayName = 'ContactFormV2Fields'

FormFields.propTypes = {
  showCommentLabel: PropTypes.bool
}

export {ERROR_LITERALS, FieldError, NAME_MAX_LENGTH, PHONE_MAX_LENGTH}

export default FormFields
